import React, { useCallback, useState } from "react";
import { AnimatePresence } from "framer-motion";
import { FaProjectDiagram, FaTrash, FaCircle } from "react-icons/fa";
import { Investigation } from "@/models/investigation";
import { createInvestigationDragPayload } from "@/utils/drag-types";
import { usePanelNavigation } from "@/hooks/use-panel-route";
import SidebarItem, { SidebarItemAction } from "./sidebar-item";
import ResourceContextMenu from "./resource-context-menu";

interface InvestigationItemProps {
  investigation: Investigation;
  onDelete?: (investigation: Investigation) => void;
  // Multi-select props
  /** Whether this item is selected */
  isSelected?: boolean;
  /** Ordered list of all investigation IDs for range selection */
  orderedIds?: string[];
  /** Callback when right-clicking on a selected item (for multi-select context menu) */
  onMultiSelectContextMenu?: (e: React.MouseEvent) => void;
}

/**
 * InvestigationItem component - displays an investigation in the sidebar.
 * Shows the investigation's name and status indicator.
 */
const InvestigationItem: React.FC<InvestigationItemProps> = ({
  investigation,
  onDelete,
  isSelected,
  orderedIds = [],
  onMultiSelectContextMenu,
}) => {
  // Panel navigation hook for split-view compatible navigation
  const { navigateTo } = usePanelNavigation();
  
  // Context menu state
  const [contextMenu, setContextMenu] = useState<{ x: number; y: number } | null>(null);

  const isActive = investigation.status === "active";
  const displayName = investigation.name || "Sans nom";

  // Create drag payload
  const dragPayload = createInvestigationDragPayload(investigation.id, displayName);

  const handleClick = useCallback(() => {
    navigateTo(`/investigations/${investigation.id}`, { title: displayName });
  }, [investigation.id, displayName, navigateTo]);

  const handleDelete = useCallback(() => {
    onDelete?.(investigation);
  }, [investigation, onDelete]);

  const handleContextMenu = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    setContextMenu({ x: e.clientX, y: e.clientY });
  }, []);

  const closeContextMenu = useCallback(() => {
    setContextMenu(null);
  }, []);

  return (
    <>
      <SidebarItem
        id={investigation.id}
        name={displayName}
        resourceType="investigation"
        icon={<FaProjectDiagram size={12} className="text-[var(--arc-accent)]" />}
        isDisabled={!isActive}
        onClick={handleClick}
        onContextMenu={handleContextMenu}
        isSelected={isSelected}
        orderedIds={orderedIds}
        onMultiSelectContextMenu={onMultiSelectContextMenu}
        enableDrag={true}
        dragPayload={dragPayload}
        hoverActions={
          onDelete && (
            <SidebarItemAction
              onClick={handleDelete}
              title="Supprimer"
              icon={<FaTrash size={10} />}
              variant="danger"
            />
          )
        }
        statusIndicator={
          <FaCircle
            size={6}
            className={isActive ? "text-green-500" : "text-gray-400"}
            title={isActive ? "Active" : "Inactive"}
          />
        }
      />

      {/* Context Menu */}
      <AnimatePresence>
        {contextMenu && (
          <ResourceContextMenu
            position={contextMenu}
            dragPayload={dragPayload}
            onClose={closeContextMenu}
          />
        )}
      </AnimatePresence>
    </>
  );
};

InvestigationItem.displayName = "InvestigationItem";

export default InvestigationItem;
